import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { usePermissions } from '../contexts/PermissionContext';
import apiService from '../services/apiService';
import { navigateToHome } from '../config/frontend';
import { 
  HardDrive, 
  Download, 
  Upload, 
  Clock, 
  Settings, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  RefreshCw,
  Calendar,
  FileText,
  Database,
  Shield,
  Zap,
  X,
  ChevronDown,
  ChevronUp,
  Play,
  Trash2,
  RotateCcw,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Eye,
  TrendingUp,
  Activity,
  Server,
  Cloud,
  Archive,
  Lock,
  Unlock,
  AlertCircle,
  Info,
  BarChart3,
  PieChart,
  Timer,
  Target,
  Layers,
  Grid3X3,
  List,
  Star,
  Bookmark,
  Tag,
  Folder,
  FolderOpen,
  File,
  FileCheck,
  FileX,
  FileCode,
  FileImage,
  FileVideo,
  FileAudio,
  FileArchive,
  FileMinus,
  FilePlus,
  FileEdit,
  FileSearch,
  FileSliders,
  FileBarChart,
  FilePieChart,
  FileLineChart,
  FileHeart,
  FileLock,
  FileKey,
  FileUser,
  FileCog,
  Users,
  Package,
  UserCheck,
  Truck,
  CreditCard,
  DollarSign,
  Receipt,
  ShoppingCart
} from 'lucide-react';
import toast from 'react-hot-toast';

const BackupRestore = () => {
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState('backup');
  const [isLoading, setIsLoading] = useState(false);
  const [backups, setBackups] = useState([]);
  const [autoBackupSettings, setAutoBackupSettings] = useState({
    enabled: false,
    frequency: 'daily',
    time: '02:00',
    retention: 30,
    includeFiles: true,
    includeDatabase: true
  });

  // Modal states
  const [showTableSelectionModal, setShowTableSelectionModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRestoreTableSelectionModal, setShowRestoreTableSelectionModal] = useState(false);
  const [showExternalRestoreModal, setShowExternalRestoreModal] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Factory reset options
  const [factoryResetOptions, setFactoryResetOptions] = useState({
    preserveUsers: false,
    preserveProducts: false,
    preserveCustomers: false,
    preserveSuppliers: false,
    preserveSales: false,
    preservePayments: false,
    preserveInventory: false,
    preserveExpenses: false,
    preservePurchases: false,
    preserveBackups: false,
    preserveSettings: false,
    preserveFiles: false,
    preserveRoles: true, // Always preserve roles when preserving super admin
    preservePermissions: true // Always preserve permissions when preserving super admin
  });

  // Progress tracking
  const [backupProgress, setBackupProgress] = useState(0);
  const [isBackupInProgress, setIsBackupInProgress] = useState(false);
  const [restoreProgress, setRestoreProgress] = useState(0);
  const [isRestoreInProgress, setIsRestoreInProgress] = useState(false);
  const [factoryResetProgress, setFactoryResetProgress] = useState(0);
  const [isFactoryResetInProgress, setIsFactoryResetInProgress] = useState(false);
  
  // Password confirmation modal
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Table selection state
  const [availableTables, setAvailableTables] = useState([
    { name: 'users', displayName: 'Users', selected: true, size: '2.5 MB' },
    { name: 'products', displayName: 'Products', selected: true, size: '15.2 MB' },
    { name: 'customers', displayName: 'Customers', selected: true, size: '3.8 MB' },
    { name: 'suppliers', displayName: 'Suppliers', selected: true, size: '1.2 MB' },
    { name: 'sales', displayName: 'Sales', selected: true, size: '25.6 MB' },
    { name: 'payments', displayName: 'Payments', selected: true, size: '8.9 MB' },
    { name: 'inventory', displayName: 'Inventory', selected: true, size: '12.3 MB' },
    { name: 'expenses', displayName: 'Expenses', selected: true, size: '4.1 MB' },
    { name: 'purchases', displayName: 'Purchases', selected: true, size: '6.7 MB' },
    { name: 'categories', displayName: 'Categories', selected: true, size: '0.8 MB' },
    { name: 'roles', displayName: 'Roles', selected: true, size: '0.3 MB' },
    { name: 'permissions', displayName: 'Permissions', selected: true, size: '0.5 MB' },
    { name: 'backups', displayName: 'Backups', selected: false, size: '45.2 MB' },
    { name: 'import_histories', displayName: 'Import Histories', selected: false, size: '2.1 MB' },
    { name: 'auto_backup_settings', displayName: 'Auto Backup Settings', selected: false, size: '0.1 MB' }
  ]);
  const [backupName, setBackupName] = useState('');
  const [backupDescription, setBackupDescription] = useState('');

  // Restore table selection state
  const [restoreTables, setRestoreTables] = useState([]);
  const [restoreTableSelection, setRestoreTableSelection] = useState({});

  // Check permissions
  const canBackup = hasPermission('system.backup');
  const canRestore = hasPermission('system.restore');
  const canDelete = hasPermission('system.delete');
  const canManage = hasPermission('system.manage');
  const canFactoryReset = hasPermission('system.factory_reset');

  useEffect(() => {
    if (canBackup || canRestore) {
      fetchBackups();
      fetchAutoBackupSettings();
      fetchAvailableTables();
    }
  }, [canBackup, canRestore]);

  const fetchAvailableTables = async () => {
    try {
      const response = await apiService.get('/system/available-tables');
      if (response.success) {
        setAvailableTables(response.data);
      }
    } catch (error) {
      console.error('Error fetching available tables:', error);
      // Keep the mock data if API fails
    }
  };

  const fetchBackupTables = async (backupId) => {
    try {
      const response = await apiService.get(`/system/backups/${backupId}/tables`);
      if (response.success) {
        setRestoreTables(response.data);
        // Initialize selection with all tables selected by default
        const initialSelection = {};
        response.data.forEach(table => {
          initialSelection[table.name] = true;
        });
        setRestoreTableSelection(initialSelection);
      }
    } catch (error) {
      console.error('Error fetching backup tables:', error);
      
      // Check if it's a 404 error (backup not found)
      if (error.message && error.message.includes('404')) {
        toast.error('Backup not found. Please refresh the backup list.');
        fetchBackups(); // Refresh backup list
        return;
      }
      
      // Fallback to available tables if API fails
      setRestoreTables(availableTables);
      const initialSelection = {};
      availableTables.forEach(table => {
        initialSelection[table.name] = true;
      });
      setRestoreTableSelection(initialSelection);
      toast.warning('Could not fetch backup tables. Using current database tables.');
    }
  };

  const fetchBackups = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.get('/system/backups');
      if (response.success) {
        setBackups(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch backups');
      }
    } catch (error) {
      console.error('Error fetching backups:', error);
      toast.error('Failed to fetch backups');
      // Set empty array on error
      setBackups([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAutoBackupSettings = async () => {
    try {
      const response = await apiService.get('/system/auto-backup-settings');
      if (response.success) {
        const settings = response.data;
        // Convert time format from "HH:MM:SS" to "HH:MM" for HTML time input
        if (settings.time && settings.time.includes(':')) {
          settings.time = settings.time.substring(0, 5);
        }
        setAutoBackupSettings(settings);
      }
    } catch (error) {
      console.error('Error fetching auto backup settings:', error);
    }
  };

  const handleManualBackup = async () => {
    if (!canBackup) {
      toast.error('You do not have permission to create backups');
      return;
    }

    // Show table selection modal
    setShowTableSelectionModal(true);
  };

  const handleCreateBackup = async () => {
    if (!canBackup) {
      toast.error('You do not have permission to create backups');
      return;
    }

    const selectedTables = availableTables.filter(table => table.selected);
    if (selectedTables.length === 0) {
      toast.error('Please select at least one table to backup');
      return;
    }

    try {
      setIsBackupInProgress(true);
      setBackupProgress(0);
      setShowTableSelectionModal(false);

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setBackupProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + Math.random() * 10;
        });
      }, 500);

      // Create backup data
      const backupData = {
        name: backupName || `backup_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}`,
        description: backupDescription,
        tables: selectedTables.map(table => table.name),
        includeFiles: true
      };

      // Make API call to create backup
      const response = await apiService.post('/system/backups', backupData);
      
      clearInterval(progressInterval);
      setBackupProgress(100);
      
      if (response.success) {
        setTimeout(() => {
          setIsBackupInProgress(false);
          setBackupProgress(0);
          toast.success('Backup created successfully');
          fetchBackups();
          setBackupName('');
          setBackupDescription('');
        }, 1000);
      } else {
        throw new Error(response.message || 'Failed to create backup');
      }

    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error('Failed to create backup');
      setIsBackupInProgress(false);
      setBackupProgress(0);
    }
  };

  const handleRestore = async (backup) => {
    if (!canRestore) {
      toast.error('You do not have permission to restore backups');
      return;
    }

    setSelectedBackup(backup);
    await fetchBackupTables(backup.id);
    setShowRestoreTableSelectionModal(true);
  };

  const handleConfirmRestore = async () => {
    if (!selectedBackup) return;

    const selectedTables = Object.keys(restoreTableSelection).filter(table => restoreTableSelection[table]);
    if (selectedTables.length === 0) {
      toast.error('Please select at least one table to restore');
      return;
    }

    try {
      setIsRestoreInProgress(true);
      setRestoreProgress(0);
      setShowRestoreTableSelectionModal(false);

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setRestoreProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + Math.random() * 15;
        });
      }, 300);
      
      const response = await apiService.post(`/system/backups/${selectedBackup.id}/restore`, {
        tables: selectedTables
      });
      
      clearInterval(progressInterval);
      setRestoreProgress(100);
      
      if (response.success) {
        setTimeout(() => {
          setIsRestoreInProgress(false);
          setRestoreProgress(0);
          toast.success('Backup restored successfully');
          setSelectedBackup(null);
          setRestoreTableSelection({});
          fetchBackups(); // Refresh backup list
        }, 1000);
      } else {
        throw new Error(response.message || 'Failed to restore backup');
      }
    } catch (error) {
      console.error('Error restoring backup:', error);
      
      // Check if it's a 404 error (backup not found)
      if (error.message && error.message.includes('404')) {
        toast.error('Backup not found. Please refresh the backup list.');
        fetchBackups(); // Refresh backup list
      } else {
        toast.error('Failed to restore backup: ' + (error.message || 'Unknown error'));
      }
      
      setIsRestoreInProgress(false);
      setRestoreProgress(0);
    }
  };

  const handleDownloadBackup = async (backupId) => {
    try {
      // Create download link
      const downloadUrl = `${import.meta.env.VITE_API_BASE_URL}/system/backups/${backupId}/download`;
      const token = localStorage.getItem('auth_token');
      
      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', '');
      
      // Add authorization header by creating a fetch request first
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Backup download started');
      } else {
        throw new Error('Download failed');
      }
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error('Failed to download backup');
    }
  };

  const handleDeleteBackup = async (backup) => {
    if (!canDelete) {
      toast.error('You do not have permission to delete backups');
      return;
    }

    setSelectedBackup(backup);
    setShowDeleteModal(true);
  };

  // External restore handlers
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.sql')) {
        toast.error('Please select a valid SQL backup file');
        return;
      }
      
      // Validate file size (max 100MB)
      if (file.size > 100 * 1024 * 1024) {
        toast.error('File size must be less than 100MB');
        return;
      }
      
      setUploadedFile(file);
    }
  };

  const handleExternalRestore = async () => {
    if (!uploadedFile) {
      toast.error('Please select a backup file to upload');
      return;
    }

    if (!canRestore) {
      toast.error('You do not have permission to restore backups');
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      const formData = new FormData();
      formData.append('backup_file', uploadedFile);
      
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 10;
        });
      }, 200);

      console.log('🔄 Sending external restore request...');
      const response = await apiService.post('/system/restore-external', formData);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.success) {
        toast.success('Backup restored successfully from external file');
        setShowExternalRestoreModal(false);
        setUploadedFile(null);
        setUploadProgress(0);
        fetchBackups(); // Refresh backup list
      } else {
        throw new Error(response.message || 'Failed to restore from external file');
      }
    } catch (error) {
      console.error('Error restoring from external file:', error);
      
      // Handle different error types
      let errorMessage = 'Failed to restore from external file';
      
      if (error.message.includes('422')) {
        errorMessage = 'Invalid file format. Please select a valid .sql backup file.';
      } else if (error.message.includes('413')) {
        errorMessage = 'File too large. Maximum size is 100MB.';
      } else if (error.message.includes('401')) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedBackup) return;

    try {
      setIsLoading(true);
      setShowDeleteModal(false);
      
      const response = await apiService.delete(`/system/backups/${selectedBackup.id}`);
      if (response.success) {
        toast.success('Backup deleted successfully');
        fetchBackups();
        setSelectedBackup(null);
      } else {
        throw new Error(response.message || 'Failed to delete backup');
      }
    } catch (error) {
      console.error('Error deleting backup:', error);
      toast.error('Failed to delete backup');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordConfirmation = async () => {
    if (!passwordConfirmation.trim()) {
      setPasswordError('Please enter your password');
      return;
    }

    try {
      // Verify password with backend
      const response = await apiService.post('/auth/verify-password', {
        password: passwordConfirmation
      });

      if (response.success) {
        setShowPasswordModal(false);
        setPasswordConfirmation('');
        setPasswordError('');
        await performFactoryReset();
      } else {
        setPasswordError('Incorrect password');
      }
    } catch (error) {
      console.error('Password verification error:', error);
      setPasswordError('Failed to verify password');
    }
  };

  const handleFactoryReset = () => {
    setShowPasswordModal(true);
    setPasswordConfirmation('');
    setPasswordError('');
  };

  const performFactoryReset = async () => {
    try {
      console.log('🔄 Starting factory reset...');
      setIsLoading(true);
      setIsFactoryResetInProgress(true);
      setFactoryResetProgress(0);
      
      
      // Scroll to top to show progress indicator
      setTimeout(() => {
        console.log('🔄 Attempting to scroll to top...');
        
        // Try multiple scroll methods for better compatibility
        const scrollMethods = [
          // Method 1: Main content area
          () => {
            const mainContent = document.querySelector('main.overflow-y-auto');
            if (mainContent) {
              console.log('📍 Found main content, scrolling...');
              mainContent.scrollTo({ top: 0, behavior: 'smooth' });
              return true;
            }
            return false;
          },
          // Method 2: Window scroll
          () => {
            console.log('📍 Using window scroll...');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return true;
          },
          // Method 3: Document element scroll
          () => {
            console.log('📍 Using document element scroll...');
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
            return true;
          },
          // Method 4: Any scrollable parent
          () => {
            const scrollableElements = document.querySelectorAll('[style*="overflow"], .overflow-y-auto, .overflow-auto');
            console.log('📍 Found scrollable elements:', scrollableElements.length);
            scrollableElements.forEach(el => {
              if (el.scrollTop > 0) {
                console.log('📍 Scrolling element:', el);
                el.scrollTo({ top: 0, behavior: 'smooth' });
              }
            });
            return true;
          }
        ];
        
        // Try each method
        for (let i = 0; i < scrollMethods.length; i++) {
          try {
            const success = scrollMethods[i]();
            if (success) {
              break;
            }
          } catch (error) {
          }
        }
      }, 100);
      
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setFactoryResetProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 10;
        });
      }, 500);
      
      const response = await apiService.post('/system/factory-reset', {
        options: factoryResetOptions
      });
      
      clearInterval(progressInterval);
      setFactoryResetProgress(100);
      
      if (response.success) {
        toast.success('Factory reset completed successfully');
        // Redirect to login page after factory reset
        setTimeout(() => {
          navigateToHome();
        }, 2000);
      } else {
        throw new Error(response.message || 'Failed to perform factory reset');
      }
    } catch (error) {
      console.error('Error performing factory reset:', error);
      toast.error('Failed to perform factory reset: ' + (error.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
      setIsFactoryResetInProgress(false);
      setFactoryResetProgress(0);
    }
  };

  const toggleFactoryResetOption = (option) => {
    setFactoryResetOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const selectAllFactoryResetOptions = () => {
    setFactoryResetOptions(prev => {
      const newOptions = {};
      Object.keys(prev).forEach(key => {
        newOptions[key] = true;
      });
      return newOptions;
    });
  };

  const deselectAllFactoryResetOptions = () => {
    setFactoryResetOptions(prev => {
      const newOptions = {};
      Object.keys(prev).forEach(key => {
        newOptions[key] = false;
      });
      return newOptions;
    });
  };

  const getSelectedFactoryResetOptionsCount = () => {
    return Object.values(factoryResetOptions).filter(Boolean).length;
  };

  const handleSaveAutoBackupSettings = async () => {
    if (!canManage) {
      toast.error('You do not have permission to manage auto backup settings');
      return;
    }

    try {
      setIsLoading(true);
      const response = await apiService.post('/system/auto-backup-settings', autoBackupSettings);
      if (response.success) {
        toast.success('Auto backup settings saved successfully');
        // Refresh the settings to get the updated data
        fetchAutoBackupSettings();
      } else {
        throw new Error(response.message || 'Failed to save auto backup settings');
      }
    } catch (error) {
      console.error('Error saving auto backup settings:', error);
      toast.error('Failed to save auto backup settings: ' + (error.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'in_progress':
        return <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
    }
  };

  const formatFileSize = (size) => {
    if (typeof size === 'number') {
      const bytes = size;
      const units = ['B', 'KB', 'MB', 'GB', 'TB'];
      let unitIndex = 0;
      let fileSize = bytes;
      
      while (fileSize >= 1024 && unitIndex < units.length - 1) {
        fileSize /= 1024;
        unitIndex++;
      }
      
      return `${fileSize.toFixed(1)} ${units[unitIndex]}`;
    }
    return size;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const toggleTableSelection = (tableName) => {
    setAvailableTables(prev => 
      prev.map(table => 
        table.name === tableName 
          ? { ...table, selected: !table.selected }
          : table
      )
    );
  };

  const selectAllTables = () => {
    setAvailableTables(prev => 
      prev.map(table => ({ ...table, selected: true }))
    );
  };

  const deselectAllTables = () => {
    setAvailableTables(prev => 
      prev.map(table => ({ ...table, selected: false }))
    );
  };

  const getSelectedTablesCount = () => {
    return availableTables.filter(table => table.selected).length;
  };

  const getTotalSelectedSize = () => {
    return availableTables
      .filter(table => table.selected)
      .reduce((total, table) => {
        const size = parseFloat(table.size);
        return total + size;
      }, 0)
      .toFixed(1);
  };

  // Restore table selection helpers
  const toggleRestoreTableSelection = (tableName) => {
    setRestoreTableSelection(prev => ({
      ...prev,
      [tableName]: !prev[tableName]
    }));
  };

  const selectAllRestoreTables = () => {
    const allSelected = {};
    restoreTables.forEach(table => {
      allSelected[table.name] = true;
    });
    setRestoreTableSelection(allSelected);
  };

  const deselectAllRestoreTables = () => {
    setRestoreTableSelection({});
  };

  const getSelectedRestoreTablesCount = () => {
    return Object.values(restoreTableSelection).filter(Boolean).length;
  };

  const getTotalSelectedRestoreSize = () => {
    return restoreTables
      .filter(table => restoreTableSelection[table.name])
      .reduce((total, table) => {
        const size = parseFloat(table.size);
        return total + size;
      }, 0)
      .toFixed(1);
  };

  // Circular Progress Component
  const CircularProgress = ({ progress, isVisible, type = 'backup' }) => {
    if (!isVisible) return null;

    const size = 90;
    const radius = (size - 12) / 2;
    const circumference = radius * 2 * Math.PI;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    const isRestore = type === 'restore';
    const gradientColors = isRestore 
      ? ['#10B981', '#059669', '#047857'] // Green gradient for restore
      : ['#3B82F6', '#8B5CF6', '#06B6D4']; // Blue gradient for backup
    
    const iconColor = isRestore ? 'text-green-600' : 'text-blue-600';
    const bgColor = isRestore ? 'bg-green-100' : 'bg-blue-100';
    const dotColor = isRestore ? 'bg-green-500' : 'bg-blue-500';
    const text = isRestore ? 'Restoring Backup...' : 'Creating Backup...';

    return (
      <div className="fixed top-20 right-6 z-50 animate-in slide-in-from-right-5 duration-300">
        {/* Main Progress Circle */}
        <div className="relative">
          {/* Background Glow Effect */}
          <div className={`absolute inset-0 bg-gradient-to-r ${isRestore ? 'from-green-400 to-emerald-500' : 'from-blue-400 to-purple-500'} rounded-full blur-lg opacity-20 scale-110`}></div>
          
          {/* Main Container */}
          <div className="relative bg-white/95 backdrop-blur-sm rounded-full shadow-2xl border border-white/20 p-4">
            {/* Outer Ring */}
            <div className="absolute inset-2 rounded-full bg-gradient-to-br from-gray-50 to-gray-100"></div>
            
            {/* Progress SVG */}
            <svg
              width={size}
              height={size}
              className="transform -rotate-90 relative z-10"
            >
              {/* Background Circle */}
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                className="text-gray-200/60"
              />
              
              {/* Progress Circle with Gradient */}
              <defs>
                <linearGradient id={`progressGradient-${type}`} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={gradientColors[0]} />
                  <stop offset="50%" stopColor={gradientColors[1]} />
                  <stop offset="100%" stopColor={gradientColors[2]} />
                </linearGradient>
              </defs>
              
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                stroke={`url(#progressGradient-${type})`}
                strokeWidth="8"
                fill="transparent"
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
                className="transition-all duration-500 ease-out drop-shadow-sm"
                strokeLinecap="round"
                style={{
                  filter: `drop-shadow(0 0 6px ${isRestore ? 'rgba(16, 185, 129, 0.3)' : 'rgba(59, 130, 246, 0.3)'})`
                }}
              />
            </svg>
            
            {/* Center Content */}
            <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
              {/* Animated Spinner */}
              <div className="relative mb-2">
                <RotateCcw className={`w-5 h-5 ${iconColor} animate-spin`} />
                <div className={`absolute inset-0 w-5 h-5 rounded-full ${bgColor} animate-ping opacity-20`}></div>
              </div>
              
              {/* Percentage */}
              <span className={`text-sm font-bold bg-gradient-to-r ${isRestore ? 'from-green-600 to-emerald-600' : 'from-blue-600 to-purple-600'} bg-clip-text text-transparent`}>
                {Math.round(progress)}%
              </span>
            </div>
          </div>
        </div>
        
        {/* Tooltip */}
        <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 animate-in fade-in-50 duration-300">
          <div className="relative">
            {/* Tooltip Arrow */}
            <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-white rotate-45 border-l border-t border-gray-200"></div>
            
            {/* Tooltip Content */}
            <div className="bg-white/95 backdrop-blur-sm px-3 py-2 rounded-lg shadow-lg border border-gray-200/50 whitespace-nowrap">
              <span className="text-xs font-medium text-gray-700 flex items-center">
                <div className={`w-2 h-2 ${dotColor} rounded-full mr-2 animate-pulse`}></div>
                {text}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };


  if (!canBackup && !canRestore) {
    return (
      <Layout>
        <div className="container mx-auto p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield size={32} className="text-yellow-600" />
            </div>
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">Access Denied</h3>
            <p className="text-yellow-700">You don't have permission to access backup and restore features.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <CircularProgress progress={backupProgress} isVisible={isBackupInProgress} type="backup" />
      <CircularProgress progress={restoreProgress} isVisible={isRestoreInProgress} type="restore" />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="container mx-auto p-6">
          {/* Modern Header */}
          <div className="mb-8 mt-16">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-2">
                  Backup & Restore
                </h1>
                <p className="text-gray-600 text-lg">Secure your data with intelligent backup management</p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-white/20 shadow-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-gray-700">System Healthy</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    fetchBackups();
                    fetchAutoBackupSettings();
                  }}
                  className="bg-white/80 backdrop-blur-sm hover:bg-white/90 text-gray-700 p-3 rounded-xl border border-white/20 shadow-lg transition-all duration-200 hover:shadow-xl"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Dashboard Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Backups</p>
                  <p className="text-3xl font-bold text-gray-900">{backups.length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Archive className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm text-green-600">
                <TrendingUp className="w-4 h-4 mr-1" />
                <span>+2 this week</span>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Storage Used</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {backups.reduce((total, backup) => total + (backup.size || 0), 0) > 1024 * 1024 
                      ? `${(backups.reduce((total, backup) => total + (backup.size || 0), 0) / (1024 * 1024)).toFixed(1)} MB`
                      : `${(backups.reduce((total, backup) => total + (backup.size || 0), 0) / 1024).toFixed(1)} KB`
                    }
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Server className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm text-blue-600">
                <Activity className="w-4 h-4 mr-1" />
                <span>Optimal usage</span>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Last Backup</p>
                  <p className="text-lg font-bold text-gray-900">
                    {backups.length > 0 ? formatDate(backups[0].created_at) : 'Never'}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm text-gray-600">
                <Calendar className="w-4 h-4 mr-1" />
                <span>Auto backup {autoBackupSettings.enabled ? 'enabled' : 'disabled'}</span>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Success Rate</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {backups.length > 0 
                      ? `${Math.round((backups.filter(b => b.status === 'completed').length / backups.length) * 100)}%`
                      : '100%'
                    }
                  </p>
                </div>
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm text-green-600">
                <CheckCircle className="w-4 h-4 mr-1" />
                <span>All systems operational</span>
              </div>
            </div>
          </div>

          {/* Modern Tab Navigation */}
          <div className="mb-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-2 border border-white/20 shadow-lg">
              <nav className="flex space-x-2">
                {canBackup && (
                  <button
                    onClick={() => setActiveTab('backup')}
                    className={`flex items-center px-6 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                      activeTab === 'backup'
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                    }`}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Backup
                  </button>
                )}
                {canRestore && (
                  <button
                    onClick={() => setActiveTab('restore')}
                    className={`flex items-center px-6 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                      activeTab === 'restore'
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                    }`}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Restore Data
                  </button>
                )}
                {canManage && (
                  <button
                    onClick={() => setActiveTab('settings')}
                    className={`flex items-center px-6 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                      activeTab === 'settings'
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                    }`}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Auto Backup
                  </button>
                )}
                {canFactoryReset && (
                  <button
                    onClick={() => setActiveTab('factory-reset')}
                    className={`flex items-center px-6 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                      activeTab === 'factory-reset'
                        ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg'
                        : 'text-red-600 hover:text-red-700 hover:bg-red-50'
                    }`}
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Factory Reset
                  </button>
                )}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'backup' && canBackup && (
            <div className="space-y-8">
              {/* Quick Actions */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Create Backup Card */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">Create New Backup</h2>
                      <p className="text-gray-600">Generate a comprehensive backup of your system</p>
                    </div>
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
                      <Plus className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Database tables</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">System files</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">User data</span>
                    </div>
                  </div>

                  <button
                    onClick={handleManualBackup}
                    disabled={isLoading || isBackupInProgress}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-4 rounded-xl hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    {isLoading || isBackupInProgress ? (
                      <>
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        Creating Backup...
                      </>
                    ) : (
                      <>
                        <Download className="w-5 h-5 mr-2" />
                        Create Backup
                      </>
                    )}
                  </button>
                </div>

                {/* Backup Info Card */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-lg">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">Backup Information</h2>
                      <p className="text-gray-600">Learn about backup features</p>
                    </div>
                    <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-600 rounded-2xl flex items-center justify-center">
                      <Info className="w-8 h-8 text-white" />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-xl p-4">
                      <div className="flex items-start space-x-3">
                        <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-blue-900">Smart Backup</h4>
                          <p className="text-blue-800 text-sm mt-1">
                            Select specific tables to include or exclude from your backup
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-xl p-4">
                      <div className="flex items-start space-x-3">
                        <Shield className="w-5 h-5 text-green-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-green-900">Secure Storage</h4>
                          <p className="text-green-800 text-sm mt-1">
                            All backups are encrypted and stored securely
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-purple-50 rounded-xl p-4">
                      <div className="flex items-start space-x-3">
                        <Zap className="w-5 h-5 text-purple-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-purple-900">Fast Recovery</h4>
                          <p className="text-purple-800 text-sm mt-1">
                            Quick restore process with minimal downtime
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Backup History */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-lg">
                <div className="px-8 py-6 border-b border-gray-200/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Backup History</h2>
                      <p className="text-gray-600 mt-1">Manage and monitor your backup files</p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search backups..."
                          className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                        <Filter className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="p-8">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-center">
                        <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
                        <span className="text-gray-500">Loading backups...</span>
                      </div>
                    </div>
                  ) : backups.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <Database className="w-10 h-10 text-gray-400" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">No backups found</h3>
                      <p className="text-gray-500 mb-6">Create your first backup to get started</p>
                      <button
                        onClick={handleManualBackup}
                        className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                      >
                        <Plus className="w-4 h-4 mr-2 inline" />
                        Create First Backup
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                      {backups.map((backup) => (
                        <div key={backup.id} className="bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 group">
                          <div className="p-6">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center space-x-3">
                                {getStatusIcon(backup.status)}
                                <div>
                                  <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                                    {backup.name}
                                  </h3>
                                  <p className="text-sm text-gray-500">
                                    {formatDate(backup.created_at)}
                                  </p>
                                </div>
                              </div>
                              <button className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
                                <MoreVertical className="w-4 h-4" />
                              </button>
                            </div>

                            <div className="space-y-3 mb-6">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">Size</span>
                                <span className="font-medium text-gray-900">{formatFileSize(backup.size)}</span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">Type</span>
                                <span className="font-medium text-gray-900 capitalize">{backup.type}</span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">Status</span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  backup.status === 'completed' 
                                    ? 'bg-green-100 text-green-800' 
                                    : backup.status === 'failed'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {backup.status}
                                </span>
                              </div>
                            </div>

                            {backup.status === 'completed' && (
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleDownloadBackup(backup.id)}
                                  className="flex-1 bg-blue-50 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium flex items-center justify-center"
                                >
                                  <Download className="w-4 h-4 mr-2" />
                                  Download
                                </button>
                                {canRestore && (
                                  <button
                                    onClick={() => handleRestore(backup)}
                                    className="flex-1 bg-green-50 text-green-600 px-4 py-2 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium flex items-center justify-center"
                                  >
                                    <RotateCcw className="w-4 h-4 mr-2" />
                                    Restore
                                  </button>
                                )}
                                {canDelete && (
                                  <button
                                    onClick={() => handleDeleteBackup(backup)}
                                    className="bg-red-50 text-red-600 px-3 py-2 rounded-lg hover:bg-red-100 transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
          </div>
        )}

        {activeTab === 'restore' && canRestore && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Restore from Backup</h2>
              <p className="text-gray-600 mb-6">
                Select a backup to restore your system to a previous state.
              </p>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 mr-3" />
                  <div>
                    <h4 className="font-medium text-red-900">Warning</h4>
                    <p className="text-red-800 text-sm mt-1">
                      Restoring a backup will replace all current data with the data from the selected backup. 
                      This action cannot be undone. Make sure to create a current backup before proceeding.
                    </p>
                  </div>
                </div>
              </div>

              {/* External Restore Option */}
              <div className="mb-6">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Upload className="w-5 h-5 text-blue-600 mr-3" />
                      <div>
                        <h3 className="font-medium text-blue-900">Restore from External File</h3>
                        <p className="text-sm text-blue-700">
                          Upload a backup file from your computer to restore the system
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowExternalRestoreModal(true)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload & Restore
                    </button>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {backups.map((backup) => (
                  <div key={backup.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Database className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <h3 className="font-medium text-gray-900">{backup.name}</h3>
                          <p className="text-sm text-gray-500">
                            {formatFileSize(backup.size)} • {formatDate(backup.created_at)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRestore(backup)}
                        className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Restore
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && canManage && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Automatic Backup Settings</h2>
                  <p className="text-gray-600 mt-1">
                    Configure automatic backup schedules to ensure your data is regularly backed up.
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${autoBackupSettings.enabled ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                  <span className="text-sm font-medium text-gray-700">
                    {autoBackupSettings.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              <div className="space-y-6">
                {/* Enable Auto Backup */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900">Enable Automatic Backups</h3>
                    <p className="text-sm text-gray-500">Automatically create backups on a schedule</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoBackupSettings.enabled}
                      onChange={(e) => setAutoBackupSettings({
                        ...autoBackupSettings,
                        enabled: e.target.checked
                      })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {autoBackupSettings.enabled && (
                  <>
                    {/* Frequency */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Backup Frequency
                      </label>
                      <select
                        value={autoBackupSettings.frequency}
                        onChange={(e) => setAutoBackupSettings({
                          ...autoBackupSettings,
                          frequency: e.target.value
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>

                    {/* Time */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Backup Time
                      </label>
                      <input
                        type="time"
                        value={autoBackupSettings.time}
                        onChange={(e) => setAutoBackupSettings({
                          ...autoBackupSettings,
                          time: e.target.value
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {/* Retention */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Retention Period (days)
                      </label>
                      <input
                        type="number"
                        value={autoBackupSettings.retention}
                        onChange={(e) => setAutoBackupSettings({
                          ...autoBackupSettings,
                          retention: parseInt(e.target.value)
                        })}
                        min="1"
                        max="365"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Backups older than this period will be automatically deleted
                      </p>
                    </div>

                    {/* Include Files */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900">Include Files</h3>
                        <p className="text-sm text-gray-500">Include uploaded files in backup</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={autoBackupSettings.includeFiles}
                          onChange={(e) => setAutoBackupSettings({
                            ...autoBackupSettings,
                            includeFiles: e.target.checked
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    {/* Include Database */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900">Include Database</h3>
                        <p className="text-sm text-gray-500">Include database in backup</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={autoBackupSettings.includeDatabase}
                          onChange={(e) => setAutoBackupSettings({
                            ...autoBackupSettings,
                            includeDatabase: e.target.checked
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </>
                )}

                {/* Action Buttons */}
                <div className="pt-4 flex space-x-3">
                  <button
                    onClick={handleSaveAutoBackupSettings}
                    disabled={isLoading}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isLoading ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Zap className="w-4 h-4 mr-2" />
                    )}
                    Save Settings
                  </button>
                  
                </div>

              </div>
            </div>
          </div>
        )}

        {activeTab === 'factory-reset' && canFactoryReset && (
          <div className="space-y-6">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-lg">
              <div className="px-8 py-6 border-b border-gray-200/50">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Factory Reset</h2>
                    <p className="text-gray-600 mt-1">Select what to preserve and what to delete during factory reset</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-red-700">Danger Zone</span>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                {/* Warning Banner */}
                <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5" />
                    <div>
                      <h3 className="text-lg font-semibold text-red-900 mb-2">⚠️ DANGER ZONE</h3>
                      <p className="text-red-800 text-sm mb-4">
                        Factory reset will permanently delete selected data from your system. 
                        This action cannot be undone. Make sure you have a backup before proceeding.
                      </p>
                      <div className="bg-red-100 rounded-lg p-3">
                        <p className="text-red-900 text-sm font-medium mb-2">What will happen:</p>
                        <ul className="text-red-800 text-sm space-y-1">
                          <li>• Selected data will be permanently deleted</li>
                          <li>• Super admin user will always be preserved</li>
                          <li>• Roles and permissions will always be preserved</li>
                          <li>• Database structure will be maintained</li>
                          <li>• You will be redirected to login page after completion</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Selection Summary */}
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-blue-900">
                        {getSelectedFactoryResetOptionsCount()} of {Object.keys(factoryResetOptions).length} options selected
                      </span>
                      <span className="text-sm text-blue-700 ml-2">
                        (Selected items will be preserved)
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={selectAllFactoryResetOptions}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        Select All
                      </button>
                      <span className="text-blue-300">|</span>
                      <button
                        onClick={deselectAllFactoryResetOptions}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                </div>

                {/* Reset Options Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  {/* Users */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 hover:border-red-300 transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <Users className="w-6 h-6 text-gray-600" />
                        <h3 className="font-semibold text-gray-900">Users</h3>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={factoryResetOptions.preserveUsers}
                          onChange={() => toggleFactoryResetOption('preserveUsers')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600">
                      Preserve all user accounts (except super admin is always preserved)
                    </p>
                  </div>

                  {/* Products */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 hover:border-red-300 transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <Package className="w-6 h-6 text-gray-600" />
                        <h3 className="font-semibold text-gray-900">Products</h3>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={factoryResetOptions.preserveProducts}
                          onChange={() => toggleFactoryResetOption('preserveProducts')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600">
                      Preserve all products and categories
                    </p>
                  </div>

                  {/* Customers */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 hover:border-red-300 transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <UserCheck className="w-6 h-6 text-gray-600" />
                        <h3 className="font-semibold text-gray-900">Customers</h3>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={factoryResetOptions.preserveCustomers}
                          onChange={() => toggleFactoryResetOption('preserveCustomers')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600">
                      Preserve all customer records
                    </p>
                  </div>

                  {/* Suppliers */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 hover:border-red-300 transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <Truck className="w-6 h-6 text-gray-600" />
                        <h3 className="font-semibold text-gray-900">Suppliers</h3>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={factoryResetOptions.preserveSuppliers}
                          onChange={() => toggleFactoryResetOption('preserveSuppliers')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600">
                      Preserve all supplier records
                    </p>
                  </div>

                  {/* Sales */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 hover:border-red-300 transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <CreditCard className="w-6 h-6 text-gray-600" />
                        <h3 className="font-semibold text-gray-900">Sales</h3>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={factoryResetOptions.preserveSales}
                          onChange={() => toggleFactoryResetOption('preserveSales')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600">
                      Preserve all sales transactions
                    </p>
                  </div>

                  {/* Payments */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 hover:border-red-300 transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <DollarSign className="w-6 h-6 text-gray-600" />
                        <h3 className="font-semibold text-gray-900">Payments</h3>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={factoryResetOptions.preservePayments}
                          onChange={() => toggleFactoryResetOption('preservePayments')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600">
                      Preserve all payment records
                    </p>
                  </div>

                  {/* Inventory */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 hover:border-red-300 transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <Archive className="w-6 h-6 text-gray-600" />
                        <h3 className="font-semibold text-gray-900">Inventory</h3>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={factoryResetOptions.preserveInventory}
                          onChange={() => toggleFactoryResetOption('preserveInventory')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600">
                      Preserve all inventory records
                    </p>
                  </div>

                  {/* Expenses */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 hover:border-red-300 transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <Receipt className="w-6 h-6 text-gray-600" />
                        <h3 className="font-semibold text-gray-900">Expenses</h3>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={factoryResetOptions.preserveExpenses}
                          onChange={() => toggleFactoryResetOption('preserveExpenses')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600">
                      Preserve all expense records
                    </p>
                  </div>

                  {/* Purchases */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 hover:border-red-300 transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <ShoppingCart className="w-6 h-6 text-gray-600" />
                        <h3 className="font-semibold text-gray-900">Purchases</h3>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={factoryResetOptions.preservePurchases}
                          onChange={() => toggleFactoryResetOption('preservePurchases')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600">
                      Preserve all purchase records
                    </p>
                  </div>

                  {/* Backups */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 hover:border-red-300 transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <Database className="w-6 h-6 text-gray-600" />
                        <h3 className="font-semibold text-gray-900">Backups</h3>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={factoryResetOptions.preserveBackups}
                          onChange={() => toggleFactoryResetOption('preserveBackups')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600">
                      Preserve all backup files
                    </p>
                  </div>

                  {/* Settings */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 hover:border-red-300 transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <Settings className="w-6 h-6 text-gray-600" />
                        <h3 className="font-semibold text-gray-900">Settings</h3>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={factoryResetOptions.preserveSettings}
                          onChange={() => toggleFactoryResetOption('preserveSettings')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600">
                      Preserve system settings
                    </p>
                  </div>

                  {/* Files */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 hover:border-red-300 transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <FileText className="w-6 h-6 text-gray-600" />
                        <h3 className="font-semibold text-gray-900">Files</h3>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={factoryResetOptions.preserveFiles}
                          onChange={() => toggleFactoryResetOption('preserveFiles')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600">
                      Preserve uploaded files
                    </p>
                  </div>

                  {/* Roles */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 hover:border-red-300 transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <UserCheck className="w-6 h-6 text-gray-600" />
                        <h3 className="font-semibold text-gray-900">Roles</h3>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={factoryResetOptions.preserveRoles}
                          onChange={() => toggleFactoryResetOption('preserveRoles')}
                          className="sr-only peer"
                          disabled={true}
                        />
                        <div className="w-11 h-6 bg-green-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600">
                      Preserve user roles (always preserved for super admin access)
                    </p>
                  </div>

                  {/* Permissions */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 hover:border-red-300 transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <Shield className="w-6 h-6 text-gray-600" />
                        <h3 className="font-semibold text-gray-900">Permissions</h3>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={factoryResetOptions.preservePermissions}
                          onChange={() => toggleFactoryResetOption('preservePermissions')}
                          className="sr-only peer"
                          disabled={true}
                        />
                        <div className="w-11 h-6 bg-green-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600">
                      Preserve system permissions (always preserved for super admin access)
                    </p>
                  </div>
                </div>

                {/* Progress Indicator - Top Right */}
                {isFactoryResetInProgress && (
                  <div className="fixed top-4 right-4 z-50 p-4 bg-red-50 border border-red-200 rounded-xl shadow-lg">
                    <div className="flex items-center space-x-4">
                      <div className="relative">
                        {/* Circular Progress */}
                        <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 100 100">
                          {/* Background Circle */}
                          <circle
                            cx="50"
                            cy="50"
                            r="35"
                            stroke="currentColor"
                            strokeWidth="6"
                            fill="none"
                            className="text-red-200"
                          />
                          {/* Progress Circle */}
                          <circle
                            cx="50"
                            cy="50"
                            r="35"
                            stroke="currentColor"
                            strokeWidth="6"
                            fill="none"
                            strokeLinecap="round"
                            strokeDasharray={`${2 * Math.PI * 35}`}
                            strokeDashoffset={`${2 * Math.PI * 35 * (1 - factoryResetProgress / 100)}`}
                            className="text-red-600 transition-all duration-500 ease-out"
                          />
                        </svg>
                        
                        {/* Center Content */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-lg font-bold text-red-900">
                              {Math.round(factoryResetProgress)}%
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Progress Info */}
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-semibold text-red-900 mb-1">Factory Reset</h3>
                        <p className="text-xs text-red-700 mb-2">Resetting system...</p>
                        
                        {/* Progress Steps */}
                        <div className="space-y-1">
                          <div className={`flex items-center space-x-2 text-xs ${factoryResetProgress >= 33 ? 'text-red-800' : 'text-red-500'}`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${factoryResetProgress >= 33 ? 'bg-red-600' : 'bg-red-300'}`}></div>
                            <span>Deleting Data</span>
                          </div>
                          <div className={`flex items-center space-x-2 text-xs ${factoryResetProgress >= 66 ? 'text-red-800' : 'text-red-500'}`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${factoryResetProgress >= 66 ? 'bg-red-600' : 'bg-red-300'}`}></div>
                            <span>Cleaning Storage</span>
                          </div>
                          <div className={`flex items-center space-x-2 text-xs ${factoryResetProgress >= 100 ? 'text-red-800' : 'text-red-500'}`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${factoryResetProgress >= 100 ? 'bg-red-600' : 'bg-red-300'}`}></div>
                            <span>Finalizing Reset</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setActiveTab('backup')}
                    disabled={isFactoryResetInProgress}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-300 rounded-xl hover:from-gray-100 hover:to-gray-200 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-[1.02]"
                  >
                    <X className="w-4 h-4 mr-2 inline" />
                    Cancel
                  </button>
                  <button
                    onClick={handleFactoryReset}
                    disabled={isLoading || isFactoryResetInProgress}
                    className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-red-600 to-red-700 border border-transparent rounded-xl hover:from-red-700 hover:to-red-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
                  >
                    {isFactoryResetInProgress ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 mr-2" />
                    )}
                    {isFactoryResetInProgress ? 'Resetting...' : 'Perform Factory Reset'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Table Selection Modal */}
        {showTableSelectionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Select Tables to Backup</h2>
                <button
                  onClick={() => setShowTableSelectionModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {/* Backup Details */}
                <div className="mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Backup Name
                      </label>
                      <input
                        type="text"
                        value={backupName}
                        onChange={(e) => setBackupName(e.target.value)}
                        placeholder="Enter backup name (optional)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <input
                        type="text"
                        value={backupDescription}
                        onChange={(e) => setBackupDescription(e.target.value)}
                        placeholder="Enter description (optional)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Selection Summary */}
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-blue-900">
                        {getSelectedTablesCount()} of {availableTables.length} tables selected
                      </span>
                      <span className="text-sm text-blue-700 ml-2">
                        (Total size: {getTotalSelectedSize()} MB)
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={selectAllTables}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        Select All
                      </button>
                      <span className="text-blue-300">|</span>
                      <button
                        onClick={deselectAllTables}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                </div>

                {/* Table List */}
                <div className="space-y-2">
                  {availableTables.map((table) => (
                    <div
                      key={table.name}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={table.selected}
                          onChange={() => toggleTableSelection(table.name)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {table.displayName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {table.name} • {table.size}
                          </div>
                        </div>
                      </div>
                      <Database className="w-5 h-5 text-gray-400" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                <button
                  onClick={() => setShowTableSelectionModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateBackup}
                  disabled={getSelectedTablesCount() === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Create Backup
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Restore Confirmation Modal */}
        {showRestoreModal && selectedBackup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Confirm Restore</h2>
                <button
                  onClick={() => setShowRestoreModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-500 mr-3" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Warning</h3>
                    <p className="text-sm text-gray-600">This action cannot be undone</p>
                  </div>
                </div>
                
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Backup Details:</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div><strong>Name:</strong> {selectedBackup.name}</div>
                    <div><strong>Size:</strong> {formatFileSize(selectedBackup.size)}</div>
                    <div><strong>Created:</strong> {formatDate(selectedBackup.created_at)}</div>
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-6">
                  Restoring this backup will replace all current data with the data from this backup. 
                  Make sure you have a current backup before proceeding.
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                <button
                  onClick={() => setShowRestoreModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmRestore}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RotateCcw className="w-4 h-4 mr-2" />
                  )}
                  Restore Backup
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedBackup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Confirm Delete</h2>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-500 mr-3" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Delete Backup</h3>
                    <p className="text-sm text-gray-600">This action cannot be undone</p>
                  </div>
                </div>
                
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Backup Details:</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div><strong>Name:</strong> {selectedBackup.name}</div>
                    <div><strong>Size:</strong> {formatFileSize(selectedBackup.size)}</div>
                    <div><strong>Created:</strong> {formatDate(selectedBackup.created_at)}</div>
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-6">
                  Are you sure you want to delete this backup? The backup file will be permanently removed from the server.
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Delete Backup
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Restore Table Selection Modal */}
        {showRestoreTableSelectionModal && selectedBackup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Select Tables to Restore</h2>
                <button
                  onClick={() => setShowRestoreTableSelectionModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {/* Backup Details */}
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Backup Details:</h4>
                  <div className="text-sm text-blue-800 space-y-1">
                    <div><strong>Name:</strong> {selectedBackup.name}</div>
                    <div><strong>Size:</strong> {formatFileSize(selectedBackup.size)}</div>
                    <div><strong>Created:</strong> {formatDate(selectedBackup.created_at)}</div>
                  </div>
                </div>

                {/* Warning */}
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 mr-3" />
                    <div>
                      <h4 className="font-medium text-red-900">Warning</h4>
                      <p className="text-red-800 text-sm mt-1">
                        Restoring selected tables will replace existing data in those tables. 
                        This action cannot be undone. Make sure to create a current backup before proceeding.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Info Notice */}
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start">
                    <Info className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
                    <div>
                      <h4 className="font-medium text-blue-900">Notice</h4>
                      <p className="text-blue-800 text-sm mt-1">
                        Table selection is currently in development. The entire backup will be restored, 
                        but your table selection will be logged for future reference.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Selection Summary */}
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-green-900">
                        {getSelectedRestoreTablesCount()} of {restoreTables.length} tables selected
                      </span>
                      <span className="text-sm text-green-700 ml-2">
                        (Total size: {getTotalSelectedRestoreSize()} MB)
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={selectAllRestoreTables}
                        className="text-sm text-green-600 hover:text-green-800"
                      >
                        Select All
                      </button>
                      <span className="text-green-300">|</span>
                      <button
                        onClick={deselectAllRestoreTables}
                        className="text-sm text-green-600 hover:text-green-800"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                </div>

                {/* Table List */}
                <div className="space-y-2">
                  {restoreTables.map((table) => (
                    <div
                      key={table.name}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={restoreTableSelection[table.name] || false}
                          onChange={() => toggleRestoreTableSelection(table.name)}
                          className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {table.displayName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {table.name} • {table.size}
                          </div>
                        </div>
                      </div>
                      <Database className="w-5 h-5 text-gray-400" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                <button
                  onClick={() => setShowRestoreTableSelectionModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmRestore}
                  disabled={getSelectedRestoreTablesCount() === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Restore Selected Tables
                </button>
              </div>
            </div>
          </div>
        )}

        {/* External Restore Modal */}
        {showExternalRestoreModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Restore from External File</h2>
                <button
                  onClick={() => {
                    setShowExternalRestoreModal(false);
                    setUploadedFile(null);
                    setUploadProgress(0);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {/* Warning */}
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 mr-3" />
                    <div>
                      <h4 className="font-medium text-red-900">Warning</h4>
                      <p className="text-red-800 text-sm mt-1">
                        Restoring from an external file will replace all current data with the data from the uploaded backup. 
                        This action cannot be undone. Make sure to create a current backup before proceeding.
                      </p>
                    </div>
                  </div>
                </div>

                {/* File Upload Area */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Backup File (.sql)
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                    <input
                      type="file"
                      accept=".sql"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="backup-file-upload"
                      disabled={isUploading}
                    />
                    <label
                      htmlFor="backup-file-upload"
                      className={`cursor-pointer ${isUploading ? 'cursor-not-allowed opacity-50' : ''}`}
                    >
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-sm text-gray-600 mb-2">
                        Click to select a backup file or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">
                        Maximum file size: 100MB
                      </p>
                    </label>
                  </div>
                  
                  {uploadedFile && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center">
                        <File className="w-5 h-5 text-green-600 mr-2" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-green-900">{uploadedFile.name}</p>
                          <p className="text-xs text-green-700">
                            Size: {formatFileSize(uploadedFile.size)}
                          </p>
                        </div>
                        <button
                          onClick={() => setUploadedFile(null)}
                          className="text-green-600 hover:text-green-800"
                          disabled={isUploading}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Upload Progress */}
                {isUploading && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Uploading and Restoring...</span>
                      <span className="text-sm text-gray-500">{Math.round(uploadProgress)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* File Requirements */}
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">File Requirements:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• File must be a valid SQL backup file (.sql extension)</li>
                    <li>• Maximum file size: 100MB</li>
                    <li>• File should contain complete database structure and data</li>
                    <li>• Ensure the backup is compatible with your current database schema</li>
                  </ul>
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowExternalRestoreModal(false);
                    setUploadedFile(null);
                    setUploadProgress(0);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleExternalRestore}
                  disabled={!uploadedFile || isUploading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isUploading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Restoring...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Restore from File
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Password Confirmation Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <Shield className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Confirm Factory Reset</h3>
                    <p className="text-sm text-gray-500">Enter your password to proceed</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordConfirmation('');
                    setPasswordError('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6">
                <div className="mb-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start space-x-3">
                      <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-medium text-red-800">Warning: This action cannot be undone</h4>
                        <p className="text-sm text-red-700 mt-1">
                          Factory reset will permanently delete all data except what you've selected to preserve. 
                          Please ensure you have a recent backup before proceeding.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter your password to confirm
                  </label>
                  <input
                    type="password"
                    value={passwordConfirmation}
                    onChange={(e) => {
                      setPasswordConfirmation(e.target.value);
                      setPasswordError('');
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                      passwordError ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your password"
                    autoFocus
                  />
                  {passwordError && (
                    <p className="mt-1 text-sm text-red-600">{passwordError}</p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordConfirmation('');
                    setPasswordError('');
                  }}
                  className="px-6 py-3 text-sm font-medium text-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-300 rounded-xl hover:from-gray-100 hover:to-gray-200 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-[1.02] flex items-center"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </button>
                <button
                  onClick={handlePasswordConfirmation}
                  disabled={!passwordConfirmation.trim()}
                  className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-red-600 to-red-700 border border-transparent rounded-xl hover:from-red-700 hover:to-red-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Confirm Factory Reset
                </button>
              </div>
            </div>
          </div>
        )}

        </div>
      </div>
    </Layout>
  );
};

export default BackupRestore;
